import { createStore, applyMiddleware } from 'redux';
import { composeWithDevTools } from 'redux-devtools-extension';
import thunk from 'redux-thunk';
import rootReducer from './reducers/index';
import setAuthToken from './utils/setAuthToken';

const initialState = {};

const middleware = [thunk];
const MAX_ARRAY_LENGTH = 200;
const MAX_STRING_LENGTH = 1000;
const MAX_OBJECT_KEYS = 100;

function sanitizeValue(value, seen = new WeakSet(), depth = 0) {
  if (value === null || value === undefined) return value;
  if (typeof value === 'function') return '[Function]';
  if (typeof value === 'string') {
    if (value.length > MAX_STRING_LENGTH) return value.slice(0, MAX_STRING_LENGTH) + `... (truncated ${value.length - MAX_STRING_LENGTH} chars)`;
    return value;
  }
  if (typeof value !== 'object') return value;
  if (seen.has(value)) return '[Circular]';
  if (depth > 5) return '[Max depth]';
  seen.add(value);

  if (Array.isArray(value)) {
    if (value.length > MAX_ARRAY_LENGTH) {
      return `[Array(${value.length})] ` + JSON.stringify(value.slice(0, 50).map(v => sanitizeValue(v, seen, depth + 1)));
    }
    return value.map((v) => sanitizeValue(v, seen, depth + 1));
  }


  const keys = Object.keys(value);
  const out = {};
  const limit = Math.min(keys.length, MAX_OBJECT_KEYS);
  for (let i = 0; i < limit; i++) {
    const k = keys[i];
    try {
      out[k] = sanitizeValue(value[k], seen, depth + 1);
    } catch (e) {
      out[k] = `[Error reading property: ${e && e.message ? e.message : e}]`;
    }
  }
  if (keys.length > limit) out._truncatedKeys = keys.length - limit;
  return out;
}

function stateSanitizer(state) {
  try {
    return sanitizeValue(state);
  } catch (e) {
    return '[State sanitization error]';
  }
}

function actionSanitizer(action) {
  try {
    // shallow clone then sanitize payload if present
    const out = { ...action };
    if (out.payload) out.payload = sanitizeValue(out.payload);
    return out;
  } catch (e) {
    return { type: action.type, _sanitizerError: String(e) };
  }
}

const composeEnhancers = composeWithDevTools({
  // friendly name shown in extension
  name: 'LearnDifferent Store',
  actionSanitizer,
  stateSanitizer,
});

const store = createStore(rootReducer, initialState, composeEnhancers(applyMiddleware(...middleware)));


// set up a store subscription listener
// to store the users token in localStorage

// initialize current state from redux store for subscription comparison
// preventing undefined error
let currentState = store.getState();

store.subscribe(() => {
  // keep track of the previous and current state to compare changes
  let previousState = currentState;
  currentState = store.getState();
  // if the token changes set the value in localStorage and axios headers
  if (previousState.auth.token !== currentState.auth.token) {
    const token = currentState.auth.token;
    setAuthToken(token);
  }
});

export default store;
